import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link as ScrollLink } from 'react-scroll'
import './Home.css'
import gsap from 'gsap'
import CyclingText from '../cyclingText/CyclingText'
import { useRetroMode } from '../../context/RetroModeContext'
import { useMagnetic } from '../../hooks/useMagnetic'
import { trackEvent } from '../../lib/analytics'
import profile from '../../images/logos/perfil.PNG'
import linkedin from '../../images/social/linkedin.png'
import github from '../../images/social/github.png'
import instagram from '../../images/social/instagram.png'

const TAGLINES = [
  'Desenvolvedor Full-Stack',
  'Apaixonado por tecnologia',
  'Criando soluções web & mobile',
  '+10 anos de experiência',
]

// The "source" pane renders this object literally  it's the same snippet
// SiteScene's floating panels show for the home stage, so the hero and the
// background read as one continuous piece of code instead of two unrelated
// decorations.
const SOURCE_LINES = [
  [
    { cls: 'kw', t: 'const' },
    { t: ' ' },
    { cls: 'tag', t: 'dev' },
    { t: ' = {' },
  ],
  [
    { t: '  ' },
    { cls: 'attr', t: 'name' },
    { t: ': ' },
    { cls: 'str', t: "'Jarder Silva'" },
    { t: ',' },
  ],
  [
    { t: '  ' },
    { cls: 'attr', t: 'role' },
    { t: ': ' },
    { cls: 'str', t: "'Full-Stack Developer'" },
    { t: ',' },
  ],
  [
    { t: '  ' },
    { cls: 'attr', t: 'stack' },
    { t: ': [' },
    { cls: 'str', t: "'React'" },
    { t: ', ' },
    { cls: 'str', t: "'Node.js'" },
    { t: ', ' },
    { cls: 'str', t: "'Laravel'" },
    { t: '],' },
  ],
  [
    { t: '  ' },
    { cls: 'attr', t: 'experience' },
    { t: ': ' },
    { cls: 'str', t: "'10+ anos'" },
    { t: ',' },
  ],
  [
    { t: '  ' },
    { cls: 'attr', t: 'focus' },
    { t: ': ' },
    { cls: 'str', t: "'web & mobile'" },
  ],
  [{ t: '}' }],
  [{ t: ' ' }],
  [
    { cls: 'kw', t: 'export' },
    { t: ' ' },
    { cls: 'kw', t: 'default' },
    { t: ' ' },
    { cls: 'tag', t: 'dev' },
  ],
]

const NAV_TABS = [
  { label: 'Sobre.jsx', to: 'sobre' },
  { label: 'Experiencias.jsx', to: 'experiencias' },
  { label: 'Projetos.jsx', to: 'projetos' },
  { label: 'Ferramentas.jsx', to: 'ferramentas' },
  { label: 'Contato.jsx', to: 'contato' },
]

const SOCIAL_LINKS = [
  {
    key: 'github',
    href: 'https://github.com/jardersilva',
    title: 'GitHub',
    icon: github,
    alt: 'Github Logo',
  },
  {
    key: 'linkedin',
    href: 'https://www.linkedin.com/in/jardersilva/',
    title: 'LinkedIn',
    icon: linkedin,
    alt: 'Linkedin Logo',
  },
  {
    key: 'instagram',
    href: 'https://www.instagram.com/jarder.dev/',
    title: 'Instagram',
    icon: instagram,
    alt: 'Instagram Logo',
  },
]

const SocialLink = ({ link }) => {
  const magnetRef = useMagnetic({ strength: 0.3 })
  return (
    <a
      ref={magnetRef}
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      title={link.title}
      onClick={() =>
        trackEvent('social_click', { network: link.key, location: 'home' })
      }
    >
      <img src={link.icon} alt={link.alt} width="34px" />
    </a>
  )
}

const Home = () => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const { retroMode } = useRetroMode()
  const rootRef = useRef(null)
  const profileRef = useRef(null)

  useEffect(() => {
    // If the image is already cached, the browser may have loaded it
    // before this onLoad handler was attached  checking `.complete`
    // catches that. A timeout is a last resort so the hero never stays
    // stuck waiting on the image forever.
    if (profileRef.current?.complete) {
      setImageLoaded(true)
    }
    const fallback = setTimeout(() => setImageLoaded(true), 2500)
    return () => clearTimeout(fallback)
  }, [])

  useLayoutEffect(() => {
    if (!imageLoaded) return undefined
    const root = rootRef.current
    if (!root) return undefined
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    if (retroMode || reduceMotion) {
      gsap.set(root.querySelectorAll('[data-reveal]'), {
        opacity: 1,
        y: 0,
        x: 0,
        scale: 1,
      })
      return undefined
    }

    const ctx = gsap.context(() => {
      gsap
        .timeline({ defaults: { ease: 'power3.out' } })
        .fromTo(
          '.ide-window',
          { opacity: 0, y: 26, scale: 0.97 },
          { opacity: 1, y: 0, scale: 1, duration: 0.7 },
        )
        .fromTo(
          '.ide-tab',
          { opacity: 0, y: -6 },
          { opacity: 1, y: 0, duration: 0.35, stagger: 0.06 },
          '-=0.35',
        )
        .fromTo(
          '.ide-source-line',
          { opacity: 0, x: -12 },
          { opacity: 1, x: 0, duration: 0.3, stagger: 0.07 },
          '-=0.15',
        )
        .fromTo(
          profileRef.current,
          { opacity: 0, scale: 0.7 },
          { opacity: 1, scale: 1, duration: 0.6 },
          '-=0.6',
        )
        .fromTo(
          '.ide-preview-name [data-word]',
          { opacity: 0, y: 18 },
          { opacity: 1, y: 0, duration: 0.5, stagger: 0.07 },
          '-=0.35',
        )
        .fromTo(
          '.ide-preview-tagline',
          { opacity: 0, y: 12 },
          { opacity: 1, y: 0, duration: 0.4 },
          '-=0.25',
        )
        .fromTo(
          '.ide-preview-links > a',
          { opacity: 0, y: 12 },
          { opacity: 1, y: 0, duration: 0.35, stagger: 0.08 },
          '-=0.2',
        )
        .fromTo(
          '.ide-statusbar',
          { opacity: 0 },
          { opacity: 1, duration: 0.3 },
          '-=0.15',
        )
    }, root)

    return () => ctx.revert()
  }, [imageLoaded, retroMode])

  return (
    <div className="ide-hero" id="home" data-scene-stage="home" ref={rootRef}>
      {retroMode && (
        <p className="retro-blink retro-welcome">
          <span role="img" aria-label="estrela">
            ✨
          </span>{' '}
          SEJA BEM-VINDO(A) AO MEU SITE PESSOAL{' '}
          <span role="img" aria-label="estrela">
            ✨
          </span>
        </p>
      )}
      <div className="ide-window" data-reveal>
        <div className="ide-tabbar">
          <span className="ide-tab ide-tab-active" data-reveal>
            <span className="ide-tab-dot" aria-hidden="true" />
            Home.jsx
          </span>
          {NAV_TABS.map((tab) => (
            <ScrollLink
              key={tab.to}
              className="ide-tab"
              data-reveal
              to={tab.to}
              smooth
              duration={500}
            >
              {tab.label}
            </ScrollLink>
          ))}
        </div>

        <div className="ide-body">
          <div className="ide-source" aria-hidden="true">
            <div className="ide-gutter">
              {SOURCE_LINES.map((_, i) => (
                <span key={`gutter-${i}`}>{i + 1}</span>
              ))}
            </div>
            <pre className="ide-code">
              {SOURCE_LINES.map((tokens, i) => (
                <div className="ide-source-line" data-reveal key={`line-${i}`}>
                  {tokens.map((token, j) =>
                    token.cls ? (
                      <span className={`tok-${token.cls}`} key={`t-${i}-${j}`}>
                        {token.t}
                      </span>
                    ) : (
                      <React.Fragment key={`t-${i}-${j}`}>
                        {token.t}
                      </React.Fragment>
                    ),
                  )}
                </div>
              ))}
              <span className="ide-cursor" />
            </pre>
          </div>

          <div className="ide-divider" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>

          <div className="ide-preview">
            <div className="ide-preview-chrome">
              <span className="ide-preview-dot" aria-hidden="true" />
              <span className="ide-preview-url">localhost:3000/jarder</span>
            </div>
            <div className="ide-preview-content">
              <img
                className="profile"
                alt="Jarder Silva"
                src={profile}
                ref={profileRef}
                onLoad={() => setImageLoaded(true)}
              />
              <h1 className="ide-preview-name greeting-text" data-reveal>
                <span className="word" data-word>
                  Olá,
                </span>{' '}
                <span className="word" data-word>
                  eu
                </span>{' '}
                <span className="word" data-word>
                  sou
                </span>{' '}
                <span className="word name" data-word>
                  Jarder
                </span>{' '}
                <span className="word name" data-word>
                  Silva
                </span>
              </h1>
              <CyclingText
                as="h2"
                className="ide-preview-tagline"
                data-reveal
                phrases={TAGLINES}
              />
              <div className="ide-preview-links" data-reveal>
                {SOCIAL_LINKS.map((link) => (
                  <SocialLink link={link} key={link.key} />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="ide-statusbar" data-reveal>
          <span className="ide-status-branch">⑂ main</span>
          <span className="ide-status-msg">
            10+ anos construindo pra web & mobile
          </span>
          <span className="ide-status-meta">UTF-8 · JSX</span>
        </div>
      </div>
    </div>
  )
}

export default Home
